/**
 * Saved WhatsApp Interactive Messages — the tenant's reusable library of List
 * and Button messages, referenced by id from the workflow canvas's send nodes
 * and (next) the AI's send_interactive_message tool.
 *
 * Sibling of WhatsAppTemplates.tsx under the shared WhatsAppSectionTabs strip,
 * not a tab inside that page — List/Button messages never go through Meta's
 * template review at all (a List Message cannot even be one; Meta's template
 * schema has no List component), so this is deliberately plain CRUD with no
 * status lifecycle to display.
 *
 * Row text (name, description) is clamped with an ellipsis so one long AI-routing
 * description can't blow a row up to a paragraph; clicking a row (or the eye icon)
 * opens a WhatsApp-bubble preview of exactly what a customer would receive.
 */
import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Alert, CircularProgress, Stack, Chip, Table, TableHead,
  TableBody, TableRow, TableCell, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText,
  Paper, TableContainer,
} from '@mui/material';
import {
  DeleteOutline, EditOutlined, FormatListBulleted, SmartButton, Add, ArrowDropDown,
  VisibilityOutlined,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import adminApi, {
  InteractiveMessage,
  InteractiveMessageKind,
  MediaAsset,
} from '@/services/admin.api';
import { usePlanGate } from '@/hooks/usePlanGate';
import StatusChip from '@/components/ui/StatusChip';
import WhatsAppSectionTabs from './whatsapp/WhatsAppSectionTabs';
import WhatsAppPreviewBubble from './campaigns/WhatsAppPreviewBubble';

const extractErr = (e: any): string =>
  e?.response?.data?.error?.message || e?.message || 'Something went wrong';

/** "Button" (Meta's own name for the message type) reads as an implementation
 *  detail to a tenant — "Message Draft" is what they're actually building:
 *  reusable body copy plus a few reply actions. Purely a display label; the
 *  underlying `kind: 'button'` value and every API/DB shape are untouched. */
function kindLabel(kind: InteractiveMessageKind): string {
  return kind === 'list' ? 'List' : 'Message Draft';
}

function kindIcon(kind: InteractiveMessageKind) {
  return kind === 'list' ? <FormatListBulleted /> : <SmartButton />;
}

/** Placeholder shown in previews of a `dynamic` message — its saved bodyText is
 *  authoring guidance for the AI, never customer-facing wording. */
const DYNAMIC_BODY_PLACEHOLDER = '[Written by the AI at send time]';

/** Clamp text to N lines with a trailing "…" (CSS-only, so the full string stays in the DOM / tooltip). */
const clampLines = (lines: number) => ({
  display: '-webkit-box',
  WebkitLineClamp: lines,
  WebkitBoxOrient: 'vertical' as const,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  wordBreak: 'break-word' as const,
});

/** Quiet row-action icon: muted until hovered, then tinted by intent. */
const quietAction = (hover: string) => ({
  color: 'text.disabled',
  '&:hover': { color: hover, backgroundColor: 'action.hover' },
});

const formatShortDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

const WhatsAppInteractiveMessages: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { guard } = usePlanGate();
  const pathPrefix = location.pathname.startsWith('/admin') ? '/admin' : '';

  const [messages, setMessages] = useState<InteractiveMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<InteractiveMessage | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [newMenuAnchor, setNewMenuAnchor] = useState<HTMLElement | null>(null);
  const [previewTarget, setPreviewTarget] = useState<InteractiveMessage | null>(null);
  // The media library is only needed to resolve a media header's URL for the
  // preview, so it is fetched lazily on the first preview that has one.
  const [mediaAssets, setMediaAssets] = useState<MediaAsset[] | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      setMessages(await adminApi.getInteractiveMessages());
    } catch (e) {
      setError(extractErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await adminApi.deleteInteractiveMessage(deleteTarget._id);
      setMessages((prev) => prev.filter((m) => m._id !== deleteTarget._id));
      setDeleteTarget(null);
      setSuccess('Interactive message deleted');
      setTimeout(() => setSuccess(''), 4000);
    } catch (e) {
      setError(extractErr(e));
    } finally {
      setDeleting(false);
    }
  };

  const openPreview = (m: InteractiveMessage) => {
    setPreviewTarget(m);
    if (m.header && mediaAssets === null) {
      adminApi.getMediaAssets().then(setMediaAssets).catch(() => setMediaAssets([]));
    }
  };

  const newPath = (kind: InteractiveMessageKind) =>
    `${pathPrefix}/whatsapp-interactive-messages/new?kind=${kind}`;

  const goCreate = (kind: InteractiveMessageKind) => {
    setNewMenuAnchor(null);
    navigate(newPath(kind));
  };

  const editPath = (m: InteractiveMessage) => `${pathPrefix}/whatsapp-interactive-messages/${m._id}`;

  const renderPreview = (m: InteractiveMessage) => {
    const dynamic = m.bodyMode === 'dynamic';
    const asset = m.header ? mediaAssets?.find((a) => a._id === m.header!.mediaAssetId) : undefined;
    const bodyText = dynamic ? DYNAMIC_BODY_PLACEHOLDER : m.bodyText;

    if (m.kind === 'list') {
      return (
        <WhatsAppPreviewBubble
          headerText={m.headerText || undefined}
          bodyText={bodyText}
          footerText={m.footerText || undefined}
          list={{
            buttonText: m.buttonText || 'Menu',
            sections: (m.sections ?? []).map((sec) => ({
              title: sec.title || undefined,
              rows: sec.rows.map((r) => ({ title: r.title, description: r.description || undefined })),
            })),
          }}
        />
      );
    }
    return (
      <WhatsAppPreviewBubble
        mediaType={m.header ? (m.header.type.toUpperCase() as 'IMAGE' | 'VIDEO' | 'DOCUMENT') : undefined}
        mediaUrl={m.header ? asset?.s3Url ?? null : null}
        mediaFileName={m.header ? asset?.originalName : undefined}
        mediaLoading={!!m.header && mediaAssets === null}
        headerText={m.header ? undefined : m.headerText || undefined}
        bodyText={bodyText}
        footerText={m.footerText || undefined}
        buttons={(m.buttons ?? []).map((b) => ({ type: 'QUICK_REPLY', text: b.title || 'Button' }))}
      />
    );
  };

  return (
    <Box>
      <WhatsAppSectionTabs active="interactive-messages" />
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Interactive Messages
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Native WhatsApp list menus and reply-button messages — built once here, sent
            from a workflow node or an AI agent.
          </Typography>
        </Box>
        <Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            endIcon={<ArrowDropDown />}
            onClick={guard('whatsappEnabled', (e: React.MouseEvent<HTMLElement>) =>
              setNewMenuAnchor(e.currentTarget)
            )}
          >
            New
          </Button>
          <Menu anchorEl={newMenuAnchor} open={!!newMenuAnchor} onClose={() => setNewMenuAnchor(null)}>
            <MenuItem onClick={() => goCreate('list')}>
              <ListItemIcon>{kindIcon('list')}</ListItemIcon>
              <ListItemText primary="List" secondary="A bottom-drawer menu of up to 10 options" />
            </MenuItem>
            <MenuItem onClick={() => goCreate('button')}>
              <ListItemIcon>{kindIcon('button')}</ListItemIcon>
              <ListItemText primary="Message Draft" secondary="Body text plus up to 3 reply buttons" />
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : messages.length === 0 ? (
        <Box textAlign="center" p={6} color="text.secondary">
          <Typography>No interactive messages yet.</Typography>
          <Typography variant="body2">
            Build a List or a Message Draft to reuse across your workflows.
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
          <Table sx={{ tableLayout: 'fixed', minWidth: 760 }}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell sx={{ width: 150 }}>Kind</TableCell>
                <TableCell sx={{ width: 210 }}>AI</TableCell>
                <TableCell sx={{ width: 130 }}>Updated</TableCell>
                <TableCell align="right" sx={{ width: 130 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {messages.map((m) => (
                <TableRow key={m._id} hover onClick={() => openPreview(m)} sx={{ cursor: 'pointer' }}>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Typography fontWeight={600} noWrap title={m.name}>
                      {m.name}
                    </Typography>
                    {m.description && (
                      <Tooltip title={m.description} placement="bottom-start" enterDelay={600}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.25, ...clampLines(2) }}
                        >
                          {m.description}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Chip size="small" icon={kindIcon(m.kind)} label={kindLabel(m.kind)} />
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Tooltip
                        title={
                          m.aiEnabled
                            ? 'An AI agent may send this message'
                            : "Off — the AI never sees this message; it's only reachable from a workflow node"
                        }
                      >
                        <span>
                          <StatusChip
                            status={m.aiEnabled ? 'active' : 'inactive'}
                            label={m.aiEnabled ? 'AI enabled' : 'AI off'}
                            dot
                          />
                        </span>
                      </Tooltip>
                      {m.bodyMode === 'dynamic' && (
                        <Tooltip title="The AI writes this message's body itself, each time — not selectable from a workflow node">
                          <span>
                            <StatusChip status="new" label="Dynamic body" />
                          </span>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top', color: 'text.secondary' }}>
                    {formatShortDate(m.updatedAt)}
                  </TableCell>
                  <TableCell align="right" sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                    <Tooltip title="Preview">
                      <IconButton
                        size="small"
                        sx={quietAction('primary.main')}
                        onClick={(e) => {
                          e.stopPropagation();
                          openPreview(m);
                        }}
                      >
                        <VisibilityOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        sx={quietAction('primary.main')}
                        onClick={(e) => {
                          e.stopPropagation();
                          guard('whatsappEnabled', () => navigate(editPath(m)))();
                        }}
                      >
                        <EditOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        sx={quietAction('error.main')}
                        onClick={(e) => {
                          e.stopPropagation();
                          guard('whatsappEnabled', () => setDeleteTarget(m))();
                        }}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!previewTarget} onClose={() => setPreviewTarget(null)} maxWidth="xs" fullWidth>
        {previewTarget && (
          <>
            <DialogTitle sx={{ pr: 2 }}>
              <Typography fontWeight={600} noWrap title={previewTarget.name}>
                {previewTarget.name}
              </Typography>
              <Stack direction="row" spacing={0.75} mt={0.75}>
                <Chip size="small" icon={kindIcon(previewTarget.kind)} label={kindLabel(previewTarget.kind)} />
                <StatusChip
                  status={previewTarget.aiEnabled ? 'active' : 'inactive'}
                  label={previewTarget.aiEnabled ? 'AI enabled' : 'AI off'}
                  dot
                />
              </Stack>
            </DialogTitle>
            <DialogContent>
              {previewTarget.bodyMode === 'dynamic' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  The AI writes the real wording at send time. Its guidance:{' '}
                  <Box component="span" sx={{ fontStyle: 'italic', ...clampLines(4) }}>
                    {previewTarget.bodyText}
                  </Box>
                </Alert>
              )}
              {renderPreview(previewTarget)}
              {previewTarget.variableCount > 0 && (
                <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
                  {previewTarget.variableCount === 1
                    ? '1 {{n}} slot is filled per send.'
                    : `${previewTarget.variableCount} {{n}} slots are filled per send.`}
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPreviewTarget(null)}>Close</Button>
              <Button
                variant="contained"
                onClick={guard('whatsappEnabled', () => {
                  const target = previewTarget;
                  setPreviewTarget(null);
                  navigate(editPath(target));
                })}
              >
                Edit
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete "{deleteTarget?.name}"?</DialogTitle>
        <DialogContent>
          <Typography>
            Any workflow node still pointing at this message will fail to send until it is
            repointed at another one. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={18} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WhatsAppInteractiveMessages;
